import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  Locale,
  LocaleDisplay,
  PageSummary,
  ShellArea,
  SiteContext,
  SiteNav,
  SiteNavItem,
  SiteSettings,
  SiteShell,
  SiteShellBlock,
} from '@icetus/contracts';
import {
  LOCALE_LABEL,
  highlightIdsOf,
  withLocalePrefix,
  type BlockHighlightTarget,
} from '@icetus/contracts';
import { Badge, Button, Card } from '@icetus/ui';
import {
  createPreviewToken,
  createTranslationJob,
  deleteShellTranslation,
  discardShellPreviewDraft,
  fetchBlocks,
  fetchPages,
  fetchShellTranslation,
  fetchSiteSettings,
  fetchSiteShell,
  fetchSites,
  fetchTranslationJob,
  saveShellPreviewDraft,
  saveShellTranslation,
  saveSiteSettings,
  saveSiteShellBlocks,
  type BlockLibraryItem,
} from '../api';
import { BlockChevron } from '../components/block-chevron';
import { BlockPropsEditor, MissingFieldsNotice } from '../components/block-fields';
import { LocaleModeRow } from '../components/locale-mode-row';
import { PreviewPane } from '../components/preview-pane';
import { openBlankTab } from '../lib/blank-tab';
import { blockSummary } from '../lib/block-summary';
import { cleanNav, describeNav, locationLabelOf } from '../lib/site-nav';
import { siteOrigin } from '../lib/site-url';

/**
 * 页头页脚编辑。
 *
 * 这个页面的存在，是为了补上一个**模型层面**的洞：主题的版式里有三类实例 ——
 * 首屏、正文、外壳（工具条/页头/页脚）。前两类落在某一页上，进 `page_blocks`；
 * 外壳不属于任何页面，以前**没有地方存**，于是控制台按页面列区块时，
 * 页头从来不在列表里，客户既看不到「主导航」，也没处填自己的网址。
 *
 * 现在外壳进 `site_blocks`，这里按区域列出来，客户改一次对全站生效。
 *
 * 两条界面上的分寸：
 *
 *   1) **不能换组件类型**。区域与类型是主题的版式决定的；让客户把导航改
 *      成商品网格，渲染出来是一个位置错误的组件，而他不会知道自己为什么改坏了。
 *      要换版式就去换主题，那是另一条路。
 *   2) **导航不在这里存 props**。导航项属于站点库（`sites.settings.nav`），
 *      按位置取 —— 主题只声明"页头有个主菜单"。给某一项改名要改菜单，
 *      不是改组件属性，否则换主题时菜单会跟着丢。
 *      菜单的增删改已经独立成「导航菜单」一页（客户的入口是"我要加一个产品分类"，
 *      不是"我要改页头第 4 块"）。这里只读它，用于摘要与预览草稿。
 */

const AREA_LABEL: Record<ShellArea, string> = {
  utility: '顶部工具条',
  header: '页头',
  footer: '页脚',
};

const AREA_HINT: Record<ShellArea, string> = {
  utility: '最上方那条细窄信息条（公告、帮助中心、电话）。多数主题只放一两项。',
  header: 'Logo、主导航、页头按钮。改这里对全站所有页面生效。',
  footer: '页脚多栏内容与版权行。',
};

const AREA_ORDER: ShellArea[] = ['utility', 'header', 'footer'];

/**
 * 草稿同步的防抖窗口。
 *
 * 与「页面内容」同一个值：每敲一个字就整页重载 iframe，会闪得像坏了；
 * 而等太久，客户会以为右侧没在跟着他改。
 */
const PREVIEW_DRAFT_DEBOUNCE_MS = 700;

interface Section {
  area: ShellArea;
  blocks: SiteShellBlock[];
}

export function ShellPage({
  tenantId,
  canRunTranslation = true,
  canEditSettings = true,
}: {
  tenantId: string;
  /** 有没有 `translation:run`。没有就藏掉「AI 翻译页头页脚」，避免点了才发现被拒 */
  canRunTranslation?: boolean;
  /**
   * 有没有 `site:update`。
   *
   * 页头页脚的**区块**由服务端按 `content:write` 判，编辑能改；但语言入口那一排
   * 按钮写的是站点设置，要 `site:update`。两个权限分开传，别合成一个布尔值 ——
   * 合成之后要么编辑改不了自己该改的区块，要么他能点到一个必然 403 的按钮。
   */
  canEditSettings?: boolean;
}) {
  const [sites, setSites] = useState<SiteContext[]>([]);
  const [siteId, setSiteId] = useState('');
  const [shell, setShell] = useState<SiteShell | null>(null);
  const [blocks, setBlocks] = useState<SiteShellBlock[]>([]);
  const [nav, setNav] = useState<SiteNav>({});
  const [library, setLibrary] = useState<BlockLibraryItem[]>([]);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /**
   * 展开编辑属性的那些块。
   *
   * 和「页面内容」一个规矩：**存展开、默认空集合**（= 全收起）。页头页脚
   * 加起来十几块，全摊开要滚好几屏，客户第一眼该看到的是"这一带由哪几块拼成"。
   * 以前这里只允许同时开一个（`openId`），但切换站点/区域时经常要对照着看，
   * 强制互斥反而碍事。
   */
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  /**
   * 右侧预览当前框住的那一块，以及"是谁点亮的"。
   *
   * 带上 blockId 是为了收起时只清掉自己点亮的那一块：客户展开 A 再展开 B，
   * 回手把 A 收起来，B 的虚线不该跟着消失。
   */
  const [active, setActive] = useState<{ blockId: string; target: BlockHighlightTarget } | null>(
    null,
  );

  /** 站点里的页面清单。只为一件事：挑一页当预览的背景 */
  const [pages, setPages] = useState<PageSummary[]>([]);
  /**
   * 预览面板是否展开。**默认展开**，与「页面内容」同一套判据。
   *
   * 改页头页脚的意义全在"右边有没有跟着变"：每次进来先点一次「预览」再动手，
   * 等于白多一步。但只在放得下两列时才默认开 —— 窄屏（≤1180px，见 styles.css
   * 里那条媒体查询）预览会变成右侧整高抽屉，默认展开等于一进来就被盖住。
   */
  const [previewOpen, setPreviewOpen] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1181px)').matches,
  );
  /** 每次自增就重新取令牌并刷新 iframe */
  const [previewReload, setPreviewReload] = useState(0);
  /** 草稿最近一次同步成功的时刻。有值说明右侧显示的是**未保存**的外壳 */
  const [draftSyncedAt, setDraftSyncedAt] = useState<Date | null>(null);
  const draftSeq = useRef(0);

  // ── 多语言外壳 ─────────────────────────────────────────────────────

  /** 站点的语言集合。语言标签只在「开了两种以上」时出现 */
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  /** 语言入口那一排按钮正在提交。只锁这一排，不锁整页的保存按钮 */
  const [localeBusy, setLocaleBusy] = useState(false);
  /** 当前编辑哪门语言。'' = 默认语言（编辑 site_blocks 本体，行为与以前完全一致） */
  const [locale, setLocale] = useState<Locale | ''>('');
  /**
   * 目标语言的覆盖层。
   *
   * `blocks` 按实例 id 存整块 props（渲染层是**整块替换**而不是逐键合并，
   * 所以这里也必须整块存）；`nav` 是整份导航的替换（结构与指向沿用默认语言，
   * 只有 label 变了）。
   *
   * `null` = 这门语言还没有任何覆盖 —— 这时界面上显示默认语言的只读副本。
   */
  const [overlay, setOverlay] = useState<{
    blocks: Map<string, Record<string, unknown>>;
    nav: SiteNav | null;
  } | null>(null);
  /** 客户真正动过的实例 id。保存时只写这几个，没动过的继续回落默认语言 */
  const [touched, setTouched] = useState<Set<string>>(() => new Set());
  const [overlayDirty, setOverlayDirty] = useState(false);
  const [overlayBusy, setOverlayBusy] = useState(false);
  /** 正在跑的「AI 翻译页头页脚」任务。有值时轮询它，跑完自动重载覆盖层 */
  const [translateJob, setTranslateJob] = useState<string | null>(null);

  const fieldsOf = useMemo(() => {
    const map = new Map<string, BlockLibraryItem>();
    for (const item of library) map.set(item.type, item);
    return map;
  }, [library]);

  useEffect(() => {
    void (async () => {
      try {
        const list = await fetchSites(tenantId);
        setSites(list);
        setSiteId((prev) => prev || list[0]?.site.id || '');
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    })();
  }, [tenantId]);

  /** 组件字段描述全租户通用，取一次就够；取不到不影响编辑，只是表单退化 */
  useEffect(() => {
    void (async () => {
      try {
        const { items } = await fetchBlocks(tenantId);
        setLibrary(items);
      } catch {
        /* 字段描述拿不到时退回通用编辑器，不该阻断整个页面 */
      }
    })();
  }, [tenantId]);

  const load = useCallback(
    async (id: string) => {
      if (!id) return;
      setBusy(true);
      setError(null);
      setNotice(null);
      try {
        const data = await fetchSiteShell(tenantId, id);
        setShell(data);
        setBlocks(data.blocks);
        setNav(data.nav);
        setDirty(false);
        setExpanded(new Set());
        setActive(null);
        setDraftSyncedAt(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusy(false);
      }
    },
    [tenantId],
  );

  useEffect(() => {
    void load(siteId);
  }, [siteId, load]);

  /**
   * 站点里的页面清单。右侧预览要一页当背景 —— 页头页脚本身不挂在任何页上，
   * 但**只能长在某一页上**才看得出效果。
   */
  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    fetchPages(tenantId, siteId)
      .then((items) => {
        if (cancelled) return;
        setPages(items);
        // 切到的这个站一页都没有：预览没有背景可看，开着只会是一块错误提示
        if (items.length === 0) setPreviewOpen(false);
      })
      .catch(() => {
        /* 拿不到页面清单只影响预览能不能开，不该阻断编辑 */
        if (!cancelled) setPages([]);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, siteId]);

  const switchSite = (id: string) => {
    if (dirty && !window.confirm('这个站点有没保存的改动，切换后会丢失。确定切过去？')) return;
    setSiteId(id);
  };

  /**
   * 站点的语言集合。
   *
   * 单独取一次设置而不是从 `fetchSiteShell` 里捎带：外壳接口返回的是"页头有哪些块"，
   * 语言属于站点配置，两件事的失效时机不同（改语言不该重拉外壳）。
   * 拿不到就当只有默认语言 —— 语言标签不出现，编辑行为与改造前完全一致。
   */
  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    fetchSiteSettings(tenantId, siteId)
      .then((next) => {
        if (!cancelled) setSettings(next);
      })
      .catch(() => {
        if (!cancelled) setSettings(null);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, siteId]);

  /**
   * 改语言入口的排布。
   *
   * **立刻提交，不进页面的脏状态**。那一排按钮是"选一个结果"而不是"填一段字"，
   * 让它跟着右上角的「保存修改」走会有两个坏处：点了按钮没反应（客户以为
   * 没生效，接着再点一次），以及——更糟的——它会被页面里别处的"放弃改动"
   * 顺手丢掉。区块是整批提交的，语言档位不是同一批东西。
   *
   * 失败要回滚：乐观改过的那一格停在"下拉"上而库里还是"自动"，
   * 客户下次进来会以为自己上次没保存成功。
   */
  const patchLocaleDisplay = async (next: LocaleDisplay) => {
    const before = settings?.localeDisplay ?? 'auto';
    if (before === next) return;
    setSettings((prev) => (prev ? { ...prev, localeDisplay: next } : prev));
    setLocaleBusy(true);
    setError(null);
    try {
      const saved = await saveSiteSettings(tenantId, siteId, { localeDisplay: next });
      setSettings(saved);
      setNotice('语言入口已更新。前台刷新后就是新样子。');
    } catch (err: unknown) {
      setSettings((prev) => (prev ? { ...prev, localeDisplay: before } : prev));
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLocaleBusy(false);
    }
  };

  /** 换站点必须退回默认语言：上一步停在阿语，切到另一个站却显示阿语标签，那是错位的 */  useEffect(() => {
    setLocale('');
    setOverlay(null);
    setTouched(new Set());
    setOverlayDirty(false);
    setTranslateJob(null);
  }, [siteId]);

  const loadOverlay = useCallback(
    async (target: Locale) => {
      try {
        const data = await fetchShellTranslation(tenantId, siteId, target);
        const hasAny = data.blocks.length > 0 || (data.nav && Object.keys(data.nav).length > 0);
        setOverlay(
          hasAny
            ? {
                blocks: new Map(data.blocks.map((block) => [block.instanceId, block.props])),
                nav: data.nav ?? null,
              }
            : null,
        );
        setTouched(new Set());
        setOverlayDirty(false);
      } catch (err: unknown) {
        setOverlay(null);
        setError(err instanceof Error ? err.message : String(err));
      }
    },
    [tenantId, siteId],
  );

  useEffect(() => {
    if (!locale) return;
    void loadOverlay(locale);
  }, [locale, loadOverlay]);

  /**
   * 轮询「AI 翻译页头页脚」任务。
   *
   * 外壳只有十几段，一次任务通常十几秒就完了，所以这里直接用 2 秒轮询 +
   * 一个上限（约 3 分钟），超时就停 —— 无限轮询会在标签页挂一整夜时
   * 变成一个每 2 秒敲一次接口的僵尸。
   */
  useEffect(() => {
    if (!translateJob || !locale) return;
    let ticks = 0;
    let stopped = false;
    const timer = window.setInterval(() => {
      ticks += 1;
      if (ticks > 90) {
        window.clearInterval(timer);
        setTranslateJob(null);
        return;
      }
      void fetchTranslationJob(tenantId, translateJob)
        .then((job) => {
          if (stopped) return;
          if (job.status === 'succeeded' || job.status === 'partial' || job.status === 'failed') {
            window.clearInterval(timer);
            setTranslateJob(null);
            setNotice(
              job.status === 'failed'
                ? `翻译失败：${job.error ?? '未知原因'}`
                : '页头页脚已翻译完成，正在载入译文…',
            );
            if (job.status !== 'failed') void loadOverlay(locale);
          }
        })
        .catch(() => {
          window.clearInterval(timer);
          setTranslateJob(null);
        });
    }, 2000);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [tenantId, translateJob, locale, loadOverlay]);

  /** 目标语言里这一块当前该显示什么：有覆盖用覆盖，没有就回落默认语言 */
  const overrideOf = (block: SiteShellBlock): Record<string, unknown> =>
    (block.instanceId ? overlay?.blocks.get(block.instanceId) : undefined) ?? block.props;

  const patchOverride = (block: SiteShellBlock, props: Record<string, unknown>) => {
    // 没有实例 id 的块挂不上覆盖层（渲染层按 instanceId 认领），编辑了也存不下去
    if (!block.instanceId) return;
    const instanceId = block.instanceId;
    setOverlay((prev) => {
      const blocks = new Map(prev?.blocks ?? []);
      blocks.set(instanceId, props);
      return { blocks, nav: prev?.nav ?? null };
    });
    setTouched((prev) => new Set(prev).add(instanceId));
    setOverlayDirty(true);
    setNotice(null);
  };

  /**
   * 改导航文案。
   *
   * 只改 label，结构（顺序、层级、指向）整份沿用默认语言那一份 —— 覆盖层的语义
   * 就是"整份导航替换"，所以这里每次都从当前默认语言导航重新生成，
   * 而不是在旧的覆盖上打补丁（那样客户在默认语言里加了一项之后，译文会错位）。
   */
  const patchNavLabel = (location: string, index: number, childIndex: number | null, label: string) => {
    const source = nav[location] ?? [];
    const clone: SiteNavItem[] = source.map((item) => ({
      ...item,
      children: item.children ? item.children.map((child) => ({ ...child })) : undefined,
    }));
    const target = childIndex === null ? clone[index] : clone[index]?.children?.[childIndex];
    if (!target) return;
    target.label = label;
    setOverlay((prev) => ({ blocks: prev?.blocks ?? new Map(), nav: { ...(prev?.nav ?? {}), [location]: clone } }));
    setOverlayDirty(true);
    setNotice(null);
  };

  const saveOverlay = async (target: Locale) => {
    setOverlayBusy(true);
    setError(null);
    setNotice(null);
    try {
      /**
       * 只提交动过的块，且 **props 用默认语言那一份作底、覆盖值覆盖上去**。
       *
       * 不能只提交覆盖里那几个键：渲染层是整块替换，少一个键前台就少一个字段
       * （版权行会消失）。用源 props 打底再合成，覆盖层才是一份完整可用的小块。
       */
      const changed = blocks.filter(
        (block): block is SiteShellBlock & { instanceId: string } =>
          Boolean(block.instanceId) && touched.has(block.instanceId!),
      );
      await saveShellTranslation(tenantId, {
        siteId,
        locale: target,
        blocks: changed.map((block) => ({
          instanceId: block.instanceId,
          props: overlay?.blocks.get(block.instanceId) ?? block.props,
        })),
        nav: overlay?.nav ?? null,
      });
      setOverlayDirty(false);
      setTouched(new Set());
      setNotice(`${LOCALE_LABEL[target]} 的页头页脚已保存。前台切到该语言即可看到。`);
      await loadOverlay(target);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setOverlayBusy(false);
    }
  };

  const runShellTranslation = async (target: Locale) => {
    setOverlayBusy(true);
    setError(null);
    try {
      const job = await createTranslationJob(tenantId, {
        siteId,
        targetLocale: target,
        kinds: ['shell'],
        strategy: overlay ? 'all' : 'missing',
        forceHuman: false,
      });
      setTranslateJob(job.id);
      setNotice('已提交。页头页脚只有十几段，通常十几秒就好。');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setOverlayBusy(false);
    }
  };

  const resetOverlay = async (target: Locale) => {
    if (!window.confirm(`删除 ${LOCALE_LABEL[target]} 的页头页脚译文？前台会回落到默认语言的内容。`)) {
      return;
    }
    setOverlayBusy(true);
    try {
      await deleteShellTranslation(tenantId, siteId, target);
      await loadOverlay(target);
      setNotice('已恢复为默认语言的内容。');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setOverlayBusy(false);
    }
  };

  /** 预览只支持默认语言（草稿通道按 site_blocks 存，没有语言维度）。切到译文就收起 */
  useEffect(() => {
    if (locale) setPreviewOpen(false);
  }, [locale]);

  const sections: Section[] = useMemo(
    () =>
      AREA_ORDER.map((area) => ({
        area,
        blocks: blocks
          .filter((block) => block.area === area)
          .sort((a, b) => a.position - b.position),
      })).filter((section) => section.blocks.length > 0),
    [blocks],
  );

  const patchBlock = (id: string, next: Partial<SiteShellBlock>) => {
    setBlocks((prev) => prev.map((block) => (block.id === id ? { ...block, ...next } : block)));
    setDirty(true);
    setNotice(null);
  };

  /**
   * 一块在预览里要框住的目标。
   *
   * 与「页面内容」同一套协议（`highlightIdsOf`）：实例 id 优先，块 id 兜底。
   * 外壳块的实例 id 是 `shared-nav` 这种，版式里那格挂的正是它。
   */
  const targetOf = (block: SiteShellBlock): BlockHighlightTarget | null => {
    const ids = highlightIdsOf(block);
    if (ids.length === 0) return null;
    return { ids, label: fieldsOf.get(block.type)?.title.zh ?? block.type };
  };

  /**
   * 展开 / 收起一块，并把右侧预览的虚线框指过去。
   *
   * 外壳的每一项（Logo、导航、页头按钮、版权行）在版式里是独立一格，
   * 整块框住就是"你在改这一格" —— 外壳没有页面区块那种"一段里几十个字段"
   * 的层级，不需要再往字段这一级收窄。
   */
  const toggleBlock = (block: SiteShellBlock) => {
    const open = expanded.has(block.id);
    setExpanded((prev) => {
      const next = new Set(prev);
      if (open) next.delete(block.id);
      else next.add(block.id);
      return next;
    });
    setActive((prev) => {
      if (open) return prev?.blockId === block.id ? null : prev;
      const target = targetOf(block);
      return target ? { blockId: block.id, target } : null;
    });
  };

  /**
   * 一块区域内全开 / 全收。
   *
   * 按区域算而不是全站一个：客户这会儿改的是页脚，把页头也一起摊开只会让
   * 滚动条更长。整片收起时顺手把预览的高亮清掉 —— 框还罩着某一块，而那一块
   * 已经收起来了，看着像没生效。
   */
  const toggleArea = (list: SiteShellBlock[], allOpen: boolean) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      for (const block of list) {
        if (allOpen) next.delete(block.id);
        else next.add(block.id);
      }
      return next;
    });
    if (allOpen) setActive(null);
  };

  /**
   * 收起态那一行摘要。
   *
   * 外壳比页面区块多一种情况：**导航的内容不在 props 里**。菜单项存在站点库
   * （`sites.settings.nav`），组件只声明"这里放 location=primary 那个菜单"。
   * 所以导航不能走 `blockSummary` —— 它会把这个位置名当成文案，摘要会显示
   * 一个 "primary"，对客户毫无意义。这里直接报菜单有几项、分别叫什么。
   *
   * 算法在 `lib/site-nav`：菜单页要显示同一句话（"已自定义 · 5 项"），
   * 两处各写一遍必然会有一处先漂。
   */
  const summaryOf = (block: SiteShellBlock): string => {
    const location = typeof block.props['location'] === 'string' ? block.props['location'] : '';
    if (location) return describeNav(nav, location);
    return blockSummary(block.props, fieldsOf.get(block.type)?.fields);
  };

  /** 区域内上下移。跨区域不允许：区域是主题的版式，客户移动等于改版式 */
  const moveBlock = (id: string, direction: -1 | 1) => {
    const target = blocks.find((block) => block.id === id);
    if (!target) return;
    const siblings = blocks
      .filter((block) => block.area === target.area)
      .sort((a, b) => a.position - b.position);
    const index = siblings.findIndex((block) => block.id === id);
    const swapWith = siblings[index + direction];
    if (!swapWith) return;

    // 用"交换下标"而不是"位置 ±1"：位置里有空洞时（回填/手工改过库），±1 会撞号
    const next = blocks.map((block) => {
      if (block.id === target.id) return { ...block, position: index + direction };
      if (block.id === swapWith.id) return { ...block, position: index };
      return block;
    });
    setBlocks(next);
    setDirty(true);
  };

  // ── 预览 ────────────────────────────────────────────────────────────

  const current = sites.find((item) => item.site.id === siteId);
  const previewHost = current?.site.primaryHost ?? null;

  /**
   * 拿哪一页当预览背景。
   *
   * 页头页脚不挂在任何页上，但只有长在某一页上才看得出效果。优先已发布页面，
   * 再优先首页：草稿页在预览里也能渲染，但客户看到"预览里的站是草稿版"
   * 容易以为整站还没上线。
   */
  const previewPage = useMemo<PageSummary | null>(() => {
    if (pages.length === 0) return null;
    const published = pages.filter((page) => page.status === 'published');
    const pool = published.length > 0 ? published : pages;
    return (
      pool.find((page) => page.kind === 'home') ??
      pool.find((page) => page.path === '/') ??
      pool[0] ??
      null
    );
  }, [pages]);

  /** 预览地址要带语言前缀，与渲染层用的是同一个函数（两边各拼一份迟早错开） */
  const previewPath =
    previewPage && current
      ? withLocalePrefix(previewPage.path, previewPage.locale, current.tenant.defaultLocale)
      : '/';

  /**
   * 预览到底摆不摆得出来：要有一页当背景、要有域名拼地址、且只支持默认语言
   * （译文的草稿通道没有语言维度）。
   *
   * 面板默认是展开的，所以这个判断不能少 —— 条件不满足时还渲染，客户看到的是
   * 右侧一整块空白，而且那颗「预览」按钮此时本来就是禁用的，他连关都关不掉。
   */
  const canPreview = Boolean(previewPage && previewHost && locale === '');
  /** 实际展开 = 客户想开 且 现在开得出来。默认值 true 只在条件满足时生效 */
  const previewShown = previewOpen && canPreview;

  const highlight = previewShown ? (active?.target ?? null) : null;

  // ── 导航 ────────────────────────────────────────────────────────────

  /**
   * 语言标签列表。
   *
   * 只列**已开通**的语言，默认语言排第一且永远在。设置还没取回来时返回空 ——
   * 空就不渲染标签行，编辑行为与改造前完全一致（这条很重要：改造不能让
   * 只用单语言的客户多看到一个会点错的入口）。
   */
  const defaultLocale = settings?.defaultLocale ?? null;
  const localeList: Locale[] =
    settings && defaultLocale
      ? [defaultLocale, ...settings.locales.filter((item) => item !== defaultLocale)]
      : [];

  const switchLocale = (next: Locale | '') => {
    if (next === locale) return;
    if ((locale ? overlayDirty : dirty) && !window.confirm('当前语言下有没保存的改动，切换后会丢失。确定切过去？')) {
      return;
    }
    setLocale(next);
    setError(null);
    setNotice(null);
  };

  const navLocations = shell?.navLocations ?? ['primary'];

  // ── 保存 ────────────────────────────────────────────────────────────

  const onSave = async () => {
    if (!siteId) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const patches = blocks.map((block) => ({
        id: block.id,
        props: block.props,
        position: block.position,
        visible: block.visible,
      }));
      /**
       * 只提交区块。导航的增删改已经搬去「导航菜单」一页，这里再顺手
       * `saveSiteNav` 一遍是危险的：客户在菜单页改完保存、回到这里展开
       * 一个页头区块再保存，会把本页内存里那份**旧的** `nav` 写回去，
       * 菜单静默回滚。这一页对 `nav` 只读。
       */
      const next = await saveSiteShellBlocks(tenantId, siteId, patches);

      setShell(next);
      setBlocks(next.blocks);
      setDirty(false);
      setDraftSyncedAt(null);
      /**
       * 正式保存之后草稿就该退休：内容已经在库里了，留着它只会让下次预览
       * 打开时先闪一帧旧内容。失败不影响保存本身，靠 TTL 兜底。
       */
      void discardShellPreviewDraft(tenantId, siteId).catch(() => {});
      // 预览面板开着的时候"保存完还要自己点刷新"是最没必要的动作
      if (previewShown) setPreviewReload((n) => n + 1);
      setNotice(
        previewShown ? '已保存，右侧预览正在刷新。' : '已保存。前台刷新即可看到新页头页脚。',
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  /**
   * 预览联动：把未保存的外壳改动同步成草稿，让右侧当真跟着变。
   *
   * 这一步不是锦上添花 —— 没有它，真实预览只能显示**已保存**的页头页脚，
   * 而保存是直接上线的。客户会退化成"改一句、保存一次、去前台看一眼"，
   * 那正是「页面内容」页在 P1-17 里解决掉的问题。
   *
   * 三处细节与页面内容一致：防抖（每敲一个字就整页重载会闪）、
   * 序号（并发 PUT 乱序返回时只认最后一次）、无改动时主动清掉草稿
   * （上次改了没保存就关预览，草稿会留在库里，下次打开先看到旧内容）。
   */
  useEffect(() => {
    if (!previewShown || !siteId) return;
    // 没有外壳可预览（当前主题不接管版式）时不发请求，只让面板显示已保存内容
    if (blocks.length === 0) return;

    if (!dirty) {
      void discardShellPreviewDraft(tenantId, siteId).catch(() => {});
      setDraftSyncedAt(null);
      return;
    }

    const target = siteId;
    const timer = window.setTimeout(() => {
      const seq = ++draftSeq.current;
      saveShellPreviewDraft(tenantId, target, { blocks, nav: cleanNav(nav) })
        .then((saved) => {
          if (seq !== draftSeq.current) return;
          setDraftSyncedAt(new Date(saved.savedAt));
          setPreviewReload((n) => n + 1);
        })
        .catch((err: unknown) => {
          if (seq !== draftSeq.current) return;
          /**
           * 草稿失败**不拦编辑**：客户还能继续改、继续保存。但要说清楚
           * 是"预览跟不上了"，否则他会以为右侧就是最终效果。
           */
          setError(
            `预览没能跟上刚才的改动：${err instanceof Error ? err.message : String(err)}。改动仍在本地，点「保存修改」后才会生效。`,
          );
        });
    }, PREVIEW_DRAFT_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [previewShown, dirty, blocks, nav, siteId, tenantId]);

  /**
   * 新标签页打开预览。
   *
   * 走 cookie 流程（不是 iframe 那条查询串）：同源导航更干净，令牌不进地址栏。
   * 空白页要在**点击的同一帧**打开 —— await 之后浏览器会把那次点击算作过期手势，
   * `window.open` 被拦截，表现为"点了没反应"。
   */
  const openPreview = async () => {
    if (!previewPage || !current) {
      setError('这个站点还没有页面，没法预览页头页脚。先在「页面内容」里建一页。');
      return;
    }
    if (!previewHost) {
      setError('这个站点还没有可访问的域名，无法预览。先到「域名绑定」页绑定一个域名。');
      return;
    }

    const tab = openBlankTab();
    if (tab) {
      try {
        tab.opener = null;
        tab.document.title = '正在准备预览…';
      } catch {
        // 拿不到句柄就算了，后面照样用它跳转
      }
    }

    try {
      const { token } = await createPreviewToken(tenantId, previewPage.id);
      const url =
        `${siteOrigin(previewHost)}/api/preview` +
        `?token=${encodeURIComponent(token)}&to=${encodeURIComponent(previewPath)}`;
      if (tab) {
        try {
          tab.location.replace(url);
        } catch {
          if (!window.open(url, '_blank')) window.location.href = url;
        }
      } else {
        window.location.href = url;
      }
    } catch (err: unknown) {
      tab?.close();
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <section className="stack">
      <div className="tablebar">
        <label className="picker">
          <span>站点</span>
          <select className="field" value={siteId} onChange={(e) => switchSite(e.target.value)}>
            {sites.map((item) => (
              <option key={item.site.id} value={item.site.id}>
                {item.site.name}
              </option>
            ))}
          </select>
        </label>
        {current ? <Badge tone="neutral">{current.site.primaryHost}</Badge> : null}
        <div className="tablebar__right">
          <Button
            variant={previewShown ? 'primary' : 'ghost'}
            onClick={() => setPreviewOpen((open) => !open)}
            aria-pressed={previewShown}
            disabled={!canPreview}
            title={
              locale !== ''
                ? '预览只支持默认语言。译文的草稿通道没有语言维度，保存后到前台切语言查看'
                : blocks.length === 0
                ? '当前主题不接管版式，页头页脚没有可预览的项'
                : !previewPage
                  ? '这个站点还没有页面，预览需要一页当背景'
                  : !previewHost
                    ? '这个站点还没有绑定域名'
                    : previewShown
                      ? '收起预览，编辑区回到整宽'
                      : '在右侧打开真实预览，改动会同步过去'
            }
          >
            {previewShown ? '收起预览' : '预览'}
          </Button>
          <Button variant="ghost" onClick={() => void load(siteId)} disabled={busy || !siteId}>
            重新载入
          </Button>
          <Button
            variant="primary"
            onClick={() => (locale ? void saveOverlay(locale) : void onSave())}
            disabled={(locale ? overlayBusy : busy) || !(locale ? overlayDirty : dirty)}
          >
            {(locale ? overlayDirty : dirty) ? '保存修改' : '已保存'}
          </Button>
        </div>
      </div>

      {/**
       * 语言标签。
       *
       * 只有开了两种以上语言才出现，且默认语言在最左 —— 客户永远是"先在原文里改，
       * 再去译文里对照"，把默认语言放中间会让人先点错。
       */}
      {localeList.length > 1 ? (
        <div className="locale-tabs">
          {localeList.map((item) => {
            const isSource = item === defaultLocale;
            return (
              <button
                key={item}
                type="button"
                className={`locale-tab${(isSource ? '' : item) === locale ? ' is-active' : ''}`}
                onClick={() => switchLocale(isSource ? '' : item)}
              >
                {LOCALE_LABEL[item]}
                {isSource ? <span className="locale-tab__tag">原文</span> : null}
                {!isSource && overlay && item === locale ? (
                  <span className="locale-tab__dot is-filled" />
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}

      {error ? <p className="error">{error}</p> : null}
      {notice ? <p className="notice">{notice}</p> : null}

      {/*
       * 左列是编辑区，右列是**真实站点预览**。
       *
       * 早先这里是画的一张结构示意图：客户改「版权行」，图上当然不会变，
       * 他得保存之后去前台找一遍才知道对不对。现在右侧就是这一页本身，
       * 展开哪一块，预览里就用虚线框住那一块（跨站只能靠 postMessage 报位置）。
       *
       * 面板 sticky 在视口顶部：客户改到页脚时它要是滚出视野，等于没有。
       * 窄屏（<1180px）下变成右侧整高抽屉，由 `.previewpane` 的媒体查询负责。
       */}
      {locale ? (
        <div className="stack">
          <Card
            title={`${LOCALE_LABEL[locale]} 的页头页脚`}
            description="翻译只换文案，不动结构：导航项的多少、顺序、指向都沿用默认语言那一份。"
          >
            {translateJob ? (
              <p className="notice">正在翻译…页头页脚只有十几段，通常十几秒就好。</p>
            ) : overlay ? (
              <p className="muted">
                这个语言已有自己的页头页脚。只改动了某几块的话，其它块继续用默认语言的内容
                —— 默认语言以后改了，那些块会跟着变。
              </p>
            ) : (
              <p className="notice notice--warn">
                下面是
                {defaultLocale ? ` ${LOCALE_LABEL[defaultLocale]} ` : '默认语言'}
                内容的只读副本，还没有 {LOCALE_LABEL[locale]} 版本。 点「AI 翻译页头页脚」生成译文后再逐条改。
              </p>
            )}
            <div className="card-actions">
              {canRunTranslation ? (
                <Button
                  variant={overlay ? 'ghost' : 'primary'}
                  onClick={() => void runShellTranslation(locale)}
                  disabled={overlayBusy || translateJob !== null || blocks.length === 0}
                >
                  {translateJob ? '翻译中…' : overlay ? '重新翻译页头页脚' : 'AI 翻译页头页脚'}
                </Button>
              ) : null}
              {overlay ? (
                <Button
                  variant="subtle"
                  onClick={() => void resetOverlay(locale)}
                  disabled={overlayBusy}
                >
                  恢复为默认语言内容
                </Button>
              ) : null}
            </div>
          </Card>

          <Card
            title="区块文案"
            description={
              overlay
                ? '展开一块改文案。这里改的是这门语言的版本，不影响默认语言。'
                : '只读副本。生成译文后才能编辑。'
            }
          >
            {blocks.length === 0 ? (
              <p className="muted">
                当前主题不接管版式，没有可编辑的外壳区块 —— 页头由「页面内容」里的区块决定，
                那些区块按页面语言翻译。
              </p>
            ) : (
              <div className="stack">
                {blocks.map((block) => {
                  const fields = fieldsOf.get(block.type);
                  const open = expanded.has(block.id);
                  return (
                    <div key={block.id} className="block-item">
                      <Card className={`block-card${open ? '' : ' is-collapsed'}`}>
                        <div className="block-head">
                          <button
                            type="button"
                            className="block-head__toggle"
                            aria-expanded={open}
                            onClick={() => toggleBlock(block)}
                          >
                            <span className="block-head__title">
                              <BlockChevron />
                              <strong>{fields?.title.zh ?? block.type}</strong>
                              <span className="block-head__type" title={`组件 ${block.type}`}>
                                {block.instanceId || block.type}
                              </span>
                            </span>
                            {open ? null : (
                              <span className="block-head__summary">{summaryOf(block)}</span>
                            )}
                          </button>
                        </div>

                        {open ? (
                          !overlay ? (
                            <p className="muted">还没有译文。先点上面的「AI 翻译页头页脚」。</p>
                          ) : fields ? (
                            <BlockPropsEditor
                              fields={fields.fields ?? []}
                              value={overrideOf(block)}
                              onChange={(next) => patchOverride(block, next)}
                              onFieldFocus={() => setActive(null)}
                            />
                          ) : (
                            <div className="fields">
                              <MissingFieldsNotice type={block.type} />
                            </div>
                          )
                        ) : null}
                      </Card>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          <Card
            title="导航文案"
            description="结构沿用默认语言：加减菜单项、改指向都在默认语言里做，这里只能改显示的文字。"
          >
            {navLocations.every((location) => (nav[location] ?? []).length === 0) ? (
              <p className="muted">
                默认语言的导航是「按已发布页面自动生成」的，没有手填文案 —— 页面标题翻了，菜单就是翻好的，不需要在这里改。
              </p>
            ) : (
              <div className="stack">
                {navLocations.map((location) => {
                  const items = nav[location] ?? [];
                  if (items.length === 0) return null;
                  return (
                    <div key={location} className="stack">
                      <b>{locationLabelOf(location)}</b>
                      <div className="stack">
                        {items.map((item, index) => (
                          <div key={`${location}-${index}`} className="locnav">
                            <NavLabelRow
                              source={item.label}
                              value={overlay?.nav?.[location]?.[index]?.label ?? item.label}
                              readOnly={!overlay}
                              onChange={(label) => patchNavLabel(location, index, null, label)}
                            />
                            {(item.children ?? []).map((child, childIndex) => (
                              <NavLabelRow
                                key={`${location}-${index}-${childIndex}`}
                                source={child.label}
                                value={
                                  overlay?.nav?.[location]?.[index]?.children?.[childIndex]?.label ??
                                  child.label
                                }
                                indent
                                readOnly={!overlay}
                                onChange={(label) =>
                                  patchNavLabel(location, index, childIndex, label)
                                }
                              />
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>
      ) : (
      <div className={`editor-split${previewShown ? ' is-split' : ''}`}>
        <div className="stack">
          <Card
            title="这些内容对全站生效"
            inline
            description="改一次，所有页面都会变。改的是你自己站里的副本，不再跟主题走。"
          >
            {/*
              这段只在**真的没东西可改**时出现。之前是无条件显示的，于是页头页脚
              明明列着七八块可编辑项，上面还写着"没有可改项" —— 客户会先怀疑是
              自己看错了，再去怀疑这个列表是假的。
            */}
            {sections.length === 0 ? (
              <p className="muted">
                没有可改项，通常说明当前主题<b>不接管版式</b>（内置的基础主题就是这样），
                页头由「页面内容」里的区块决定。换成带版式的主题后，这里会出现
                Logo、主导航、页脚等可编辑项。
              </p>
            ) : null}
          </Card>

          {sections.map((section) => (
            <Card
              key={section.area}
              title={AREA_LABEL[section.area]}
              description={AREA_HINT[section.area]}
              inline
            >
              <div className="stack">
                {/*
                  语言入口不在这张列表里 —— 它由渲染层在页头右角自动补，不是客户
                  拖进去的区块（页头列表里凭空多一项反而会让人以为能删）。
                  所以在列表**上面**给它一排真控件：客户到这一页是来改页头的，
                  "页头上还有什么"这件事应该在这儿能改，而不是被指去另一个页面。
                */}
                {section.area === 'header' && settings ? (
                  <LocaleModeRow
                    locales={settings.locales}
                    defaultLocale={settings.defaultLocale}
                    value={settings.localeDisplay}
                    busy={localeBusy}
                    editable={canEditSettings}
                    onChange={(next) => void patchLocaleDisplay(next)}
                  />
                ) : null}
                {(() => {
                  /*
                   * 「全部展开/收起」按区域算，不是全站一个：客户这会儿改的是页脚，
                   * 把页头也一起摊开只会让滚动条更长。表头的计数同理。
                   */
                  const allOpen = section.blocks.every((block) => expanded.has(block.id));
                  return (
                    <div className="blocklist-bar">
                      <span className="muted">
                        共 {section.blocks.length} 个区块
                        {allOpen ? '' : ' · 点区块左侧的三角展开'}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleArea(section.blocks, allOpen)}
                      >
                        {allOpen ? '全部收起' : '全部展开'}
                      </Button>
                    </div>
                  );
                })()}

                {section.blocks.map((block, index) => {
                  const fields = fieldsOf.get(block.type);
                  const open = expanded.has(block.id);
                  // 摘要只给收起的行：展开时字段就在下面，多一行只是重复
                  const summary = open ? '' : summaryOf(block);
                  return (
                    <div key={block.id} className="block-item">
                      <Card className={`block-card${open ? '' : ' is-collapsed'}`}>
                        <div className="block-head">
                          <button
                            type="button"
                            className="block-head__toggle"
                            aria-expanded={open}
                            onClick={() => toggleBlock(block)}
                            title={open ? '收起这一块' : '展开这一块'}
                          >
                            <span className="block-head__title">
                              <BlockChevron />
                              <strong>{fields?.title.zh ?? block.type}</strong>
                              {/*
                               * 只留一个等宽标识：实例 id。主题按它认领这一块（上架校验
                               * 也认它），排查"为什么改了没生效"要对的就是它。
                               * 组件名不再并排显示（`site-logo shared-logo` 两串 11px 等宽字
                               * 在十行一列的页头里很吵），放进 title 备查；
                               * 老数据没有实例 id 时退回组件名，这一格不留空。
                               */}
                              <span className="block-head__type" title={`组件 ${block.type}`}>
                                {block.instanceId || block.type}
                              </span>
                              {block.visible ? null : (
                                <span className="block-head__off">已隐藏</span>
                              )}
                            </span>
                            {summary ? (
                              <span className="block-head__summary" title={summary}>
                                {summary}
                              </span>
                            ) : null}
                          </button>
                          <div className="block-head__actions">
                            <label className="check">
                              <input
                                type="checkbox"
                                checked={block.visible}
                                onChange={(e) =>
                                  patchBlock(block.id, { visible: e.target.checked })
                                }
                              />
                              <span>显示</span>
                            </label>
                            <Button
                              variant="ghost"
                              onClick={() => moveBlock(block.id, -1)}
                              disabled={index === 0}
                              title="在区域内上移"
                            >
                              上移
                            </Button>
                            <Button
                              variant="ghost"
                              onClick={() => moveBlock(block.id, 1)}
                              disabled={index === section.blocks.length - 1}
                              title="在区域内下移"
                            >
                              下移
                            </Button>
                          </div>
                        </div>

                        {open ? (
                          fields ? (
                            <BlockPropsEditor
                              fields={fields.fields ?? []}
                              value={block.props}
                              onChange={(next) => patchBlock(block.id, { props: next })}
                              onFieldFocus={() =>
                                /**
                                 * 光标进了某一块的字段，预览就该框住这一块 —— 包括
                                 * 通过「全部展开」摊开、从没点过三角的那种情况。
                                 * 同一块返回原引用：否则在一个块里连点三个输入框，
                                 * 预览会重复发消息、反复平滑滚动，看着像在抖。
                                 */
                                setActive((prev) => {
                                  if (prev?.blockId === block.id) return prev;
                                  const target = targetOf(block);
                                  return target ? { blockId: block.id, target } : prev;
                                })
                              }
                            />
                          ) : (
                            <div className="fields">
                              <MissingFieldsNotice type={block.type} />
                            </div>
                          )
                        ) : null}
                      </Card>
                    </div>
                  );
                })}
              </div>
            </Card>
          ))}

        </div>

        {previewShown ? (
          <PreviewPane
            tenantId={tenantId}
            pageId={previewPage?.id ?? ''}
            path={previewPath}
            host={previewHost}
            reloadSignal={previewReload}
            dirty={dirty}
            draftAt={draftSyncedAt}
            highlight={highlight}
            onClose={() => setPreviewOpen(false)}
            onOpenTab={() => void openPreview()}
          />
        ) : null}
      </div>
      )}
    </section>
  );
}

/**
 * 一行的导航译文：左边是默认语言的原文，右边是这门语言的输入框。
 *
 * 特意把原文并排显示：菜单项只有"About"这么两个字，客户看不到原文
 * 根本不知道自己在翻哪一项（顺序与默认语言一致，但他记不住顺序）。
 */
function NavLabelRow({
  source,
  value,
  onChange,
  indent,
  readOnly,
}: {
  source: string;
  value: string;
  onChange: (label: string) => void;
  indent?: boolean;
  readOnly: boolean;
}) {
  return (
    <div className={`locnav__row${indent ? ' is-child' : ''}`}>
      <span className="locnav__source" title={source}>
        {source || '（未填文案）'}
      </span>
      {readOnly ? (
        <span className="locnav__locked">未翻译</span>
      ) : (
        <input
          className="field"
          value={value}
          placeholder={source}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </div>
  );
}
